import { Phone, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WhatsAppIcon } from '@/components/property/ContactIcons';
import { toast } from 'sonner';

interface HeaderProps {
  logoUrl?: string | null;
  agencyName: string;
  agentName?: string;
  refId: string;
}

export function ListingContactHeader({ logoUrl, agencyName, agentName, refId }: HeaderProps) {
  return (
    <div className="flex flex-col items-center text-center pb-4 mb-4 border-b">
      {logoUrl ? (
        <img src={logoUrl} alt={agencyName} className="h-20 w-auto max-w-[160px] object-contain mb-2" />
      ) : (
        <div className="h-20 w-20 rounded-lg bg-primary/10 border-2 border-primary/20 flex items-center justify-center text-primary font-bold text-2xl mb-2">
          {agencyName.charAt(0)}
        </div>
      )}
      <div className="font-bold text-base leading-tight">{agencyName}</div>
      {agentName && (
        <div className="text-sm text-foreground/80 mt-1">
          <span className="text-muted-foreground">Agent:</span>{' '}
          <span className="font-semibold">{agentName}</span>
        </div>
      )}
      <div className="text-xs text-muted-foreground mt-0.5">EthioRealEstates - {refId}</div>
    </div>
  );
}

interface ActionsProps {
  phone: string;
}

export function ListingContactActions({ phone }: ActionsProps) {
  const copy = () => {
    navigator.clipboard.writeText(phone);
    toast.success('Number copied to clipboard');
  };
  return (
    <>
      <Button
        asChild
        className="w-full h-12 mb-2 bg-[#25D366] hover:bg-[#1fb955] text-white font-bold text-base gap-2 rounded-md"
      >
        <a href={`tel:${phone}`}>
          <Phone className="h-5 w-5" /> Call
        </a>
      </Button>
      <div className="grid grid-cols-2 gap-2 mb-5">
        <Button
          variant="outline"
          className="h-10 font-semibold text-xs border-primary/30 hover:bg-primary/5 gap-2"
          asChild
        >
          <a
            href={`https://wa.me/${phone.replace(/[^0-9]/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <WhatsAppIcon className="h-4 w-4" />
            WhatsApp
          </a>
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-10 font-semibold text-xs border-primary/30 hover:bg-primary/5 gap-2"
          onClick={copy}
        >
          <Copy className="h-4 w-4" />
          Copy Number
        </Button>
      </div>
    </>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  htmlFor?: string;
  children: React.ReactNode;
}

/** Zameen-style stacked filled field: uppercase label on top, input below, inside a grey card. */
export function ZameenField({ label, required, htmlFor, children }: FieldProps) {
  return (
    <label
      htmlFor={htmlFor}
      className="block rounded-md border bg-muted/40 px-3 pt-2 pb-1.5 focus-within:border-primary focus-within:bg-background transition-colors cursor-text"
    >
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
        {required && <span className="text-destructive">*</span>}
      </div>
      {children}
    </label>
  );
}

/** Base input class to drop into ZameenField (borderless, transparent). */
export const zameenInputClass =
  'w-full border-0 bg-transparent shadow-none px-0 h-8 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 outline-none placeholder:text-muted-foreground/60';

export const zameenTextareaClass =
  'w-full border-0 bg-transparent shadow-none px-0 py-1 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 outline-none resize-none placeholder:text-muted-foreground/60';
